// src/pages/imoveis/ImovelForm.tsx
import React, { useState, useEffect } from 'react';
import { useParams} from 'react-router-dom';
import { apiRequest } from '../../services/apiService';
import { Imovel, TipoImovel, StatusImovel, TipoAnuncio, FotosImovel } from '../../data/Imovel';
import ImovelDadosPrincipaisForm from './ImovelDadosPrincipaisForm';
import ImovelFotosForm from './ImovelFotosForm';
import ImovelFotoEditModal from './ImovelFotoEditModal';
import { Comodidade } from '../../data/Comodidades'; // Importa a interface Comodidade
import ConfirmationModal from '../../componets/ConfirmationModal';

export interface FotoUpload {
  arquivo: File;
  nome: string;
  descricao: string;
}

function ImovelForm() {
  const { id } = useParams<{ id: string }>();
  const [imovel, setImovel] = useState<Partial<Imovel>>({
    titulo: '',
    descricao: '',
    tipo: TipoImovel.Casa,
    status: StatusImovel.Disponivel,
    tipoAnuncio: TipoAnuncio.Venda,
    cep: '',
    logradouro: '',
    bairro: '',
    cidade: '',
    uf: '',
    areaUtil: 0,
    areaTotal: 0,
    quartos: 1,
    suites: 0,
    banheiros: 1,
    vagasGaragem: 0,
    valor: 0,
    proprietarioId: 0,
    destaque: false,
    fotos: [],
    comodidades: []
  });
  const [activeTab, setActiveTab] = useState('dados');
  const [selectedFiles, setSelectedFiles] = useState<FotoUpload[]>([]);
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<number | null>(null);
  const [showEditPhotoModal, setShowEditPhotoModal] = useState(false);
  const [photoToEdit, setPhotoToEdit] = useState<FotosImovel | null>(null);  

  const imovelId = id || imovel.id;

  useEffect(() => {
    if (id) {
      apiRequest<Imovel>(`imovel/${id}`, 'GET', true)
        .then(data => setImovel(data))
        .catch(err => setError((err as Error).message || 'Erro ao carregar o imóvel.'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    let processedValue: string | number | boolean | string[] = value;

    if (type === 'checkbox') {
      processedValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'number') {
      processedValue = value === '' ? 0 : parseFloat(value);
    }

    setImovel(prevImovel => ({
      ...prevImovel,
      [name]: processedValue,
    }));
  };

  const handleCaracteristicasChange = (selectedComodidades: Comodidade[]) => {
    setImovel(prevImovel => ({
      ...prevImovel,
      comodidades: selectedComodidades, // Atualiza a propriedade 'comodidades'
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles: FotoUpload[] = Array.from(e.target.files).map(file => ({
        arquivo: file,
        nome: file.name, // Usa o nome do arquivo como padrão
        descricao: ''
      }));
      setSelectedFiles(prevFiles => [...prevFiles, ...newFiles]);
      // Limpa o valor do input para permitir selecionar o mesmo arquivo novamente
      e.target.value = '';
    }
  };

  const handleNewFileMetaChange = (index: number, field: 'nome' | 'descricao', value: string) => {
    setSelectedFiles(prevFiles =>
      prevFiles.map((file, i) =>
        i === index ? { ...file, [field]: value } : file
      ));
  };

  const handleRemoveNewFile = (fileToRemove: FotoUpload) => {
    setSelectedFiles(prev => prev.filter(f => f.arquivo !== fileToRemove.arquivo));
  };

    const handleEditExistingFile = (foto: FotosImovel) => {
    setPhotoToEdit(foto);
    setShowEditPhotoModal(true);
  };

  const handleDadosSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const imovelSalvo = await apiRequest<Imovel>('imovel', 'POST', true, imovel);
      setImovel(imovelSalvo); // Atualiza o estado com o imóvel salvo

      
      // Atualiza a URL para o modo de edição sem recarregar a página
      window.history.replaceState(null, '', `/imoveis/editar/${imovelSalvo.id}`);
      setActiveTab('fotos'); // Muda para a aba de fotos para incentivar o upload
      
    } catch (err) {
      setError((err as Error).message || 'Erro ao salvar o imóvel.');
    } finally {
      setLoading(false);
    }
  };

  // Abre o modal de confirmação
  const handleRemoveExistingFile = (fotoId: number) => {
    setPhotoToDelete(fotoId);
    setShowDeleteModal(true);
  };

  // Executa a exclusão após a confirmação
  const confirmDeletePhoto = async () => {
    if (!photoToDelete) return;

    setShowDeleteModal(false);
    setLoading(true);
    setError(null);

    try {
      await apiRequest(`imovel/deletefotos/${photoToDelete}`, 'POST', true, null);

      setImovel(prevImovel => ({
        ...prevImovel,
        fotos: prevImovel.fotos?.filter(foto => foto.id !== photoToDelete)
      }));
      setPhotoToDelete(null);

    } catch (err) {
      setError((err as Error).message || 'Erro ao excluir a foto.');
    } finally {
      setLoading(false);
      setPhotoToDelete(null); // Limpa o ID mesmo em caso de erro
    }
  };

  const handleUpdatePhotoDetails = async (updatedPhoto: FotosImovel) => {
    if (!updatedPhoto || !updatedPhoto.id) return;

    setLoading(true);
    setError(null);

    try {
      await apiRequest('imovel/foto', 'PUT', true, {
        id: updatedPhoto.id,
        descricao: updatedPhoto.descricao,
        destaque: updatedPhoto.destaque,
      });

      // Atualiza o estado local do imóvel com a foto modificada
      setImovel(prevImovel => ({
        ...prevImovel,
        fotos: prevImovel.fotos?.map(f => f.id === updatedPhoto.id ? updatedPhoto : f)
      }));

      setShowEditPhotoModal(false); // Fecha o modal em caso de sucesso
    } catch (err) {
      setError((err as Error).message || 'Erro ao atualizar os detalhes da foto.');
    } finally {
      setLoading(false);
    }
  };
  const handleFotosSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!imovelId) {
      setError('É necessário salvar os dados do imóvel antes de enviar as fotos.');
      return;
    }
    if (selectedFiles.length === 0) {
      setError('Nenhuma nova foto foi selecionada para envio.');
      return;
    }

    setLoading(true);
    setError(null);

    const formData = new FormData();
    // Adiciona o ID do imóvel para associar as fotos
    // A chave deve corresponder EXATAMENTE à propriedade no ViewModel do .NET (ImovelId)
    formData.append('ImovelId', String(imovelId));
    selectedFiles.forEach((fotoUpload, index) => {
      // O prefixo da lista (Fotos) e os nomes das propriedades (Arquivo, Descricao) também devem corresponder.
      formData.append(`Fotos[${index}].Arquivo`, fotoUpload.arquivo);
      formData.append(`Fotos[${index}].NomeArquivoOriginal`, fotoUpload.arquivo.name);
      formData.append(`Fotos[${index}].Descricao`, fotoUpload.descricao);
    });

    try {
      // IMPORTANTE: A função `apiRequest` deve ser adaptada para lidar com `FormData`.
      // Ela NÃO deve definir 'Content-Type': 'application/json' e não deve usar JSON.stringify no corpo
      // quando o corpo é uma instância de FormData. O navegador definirá o cabeçalho multipart/form-data correto.
      const endpoint = 'imovel/fotos';
      const updatedImovel = await apiRequest<Imovel>(endpoint, 'POST', false, formData);
      
      setImovel(updatedImovel); // Atualiza o estado com a nova lista de fotos
      setSelectedFiles([]); // Limpa os arquivos selecionados
    } catch (err) {
      setError((err as Error).message || 'Erro ao enviar as fotos.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && imovelId) {
    return <p>Carregando...</p>;
  }

  return (
    <div className="row">
      <div className="card">
        <div className="card-body">
          <div className="mb-3">
            <h1 className="h3 d-inline align-middle">{imovelId ? 'Editar Imóvel' : 'Novo Imóvel'}</h1>
          </div>
          {error && <div className="alert alert-danger">Erro: {error}</div>}

          <ul className="nav nav-tabs" role="tablist">
            <li className="nav-item" role="presentation">
              <button 
                className={`nav-link ${activeTab === 'dados' ? 'active' : ''}`}
                onClick={() => setActiveTab('dados')}
                type="button"
              >
                Dados do Imóvel
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button 
                className={`nav-link ${activeTab === 'fotos' ? 'active' : ''}`}
                onClick={() => setActiveTab('fotos')}
                type="button"
              >
                Fotos
              </button>
            </li>
          </ul>

          
            <div className="tab-content pt-3">
              {/* DADOS DO IMÓVEL TAB */}
              <div className={`tab-pane fade ${activeTab === 'dados' ? 'show active' : ''}`}>
                <ImovelDadosPrincipaisForm
                  imovel={imovel}
                  handleChange={handleChange}
                  handleCaracteristicasChange={handleCaracteristicasChange}
                  handleSubmit={handleDadosSubmit}
                  loading={loading}
                  imovelId={imovelId}
                />
              </div>

              {/* FOTOS TAB */}
              <div className={`tab-pane fade ${activeTab === 'fotos' ? 'show active' : ''}`}>
                <ImovelFotosForm
                  imovel={imovel}
                  selectedFiles={selectedFiles}
                  handleFileChange={handleFileChange}
                  handleRemoveNewFile={handleRemoveNewFile}
                  handleRemoveExistingFile={handleRemoveExistingFile}
                  handleEditExistingFile={handleEditExistingFile}
                  handleNewFileMetaChange={handleNewFileMetaChange}
                  handleSubmit={handleFotosSubmit}
                  loading={loading}
                  imovelId={imovelId}
                />
              </div>

              <ConfirmationModal
                show={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDeletePhoto}
                title="Confirmar Exclusão"
                body="Tem certeza que deseja excluir esta foto? Esta ação não pode ser desfeita."
                confirmText="Excluir"
                confirmButtonVariant="danger"
              />

              <ImovelFotoEditModal
                show={showEditPhotoModal}
                onClose={() => setShowEditPhotoModal(false)}
                onSave={handleUpdatePhotoDetails}
                foto={photoToEdit}
                loading={loading}
              />
            </div>
        </div>
      </div>
    </div>
 );
}

export default ImovelForm;
